/**
 * Shared URL helpers for `/exam/viewer` deep links + return navigation.
 *
 * Return destination is kept in sessionStorage (not in the query string) so
 * the address bar stays clean / professional.
 */

export type ExamViewerTab = 'generalViewer' | 'views' | 'report';

const DEFAULT_RETURN = '/exams';
const RETURN_STORAGE_KEY = 'deepecho:examViewerReturn';
const REPORT_SECTION_STORAGE_KEY = 'deepecho:lastReportSection';

/** Only allow same-app relative paths (blocks open redirects). */
export function sanitizeReturnUrl(
  raw: string | null | undefined,
  fallback: string = DEFAULT_RETURN
): string {
  if (!raw) return fallback;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return fallback;
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return fallback;
  if (decoded.includes('://')) return fallback;
  return decoded;
}

/** Persist where the user came from before opening the exam viewer. */
export function storeExamViewerReturn(path: string | null | undefined): void {
  try {
    sessionStorage.setItem(
      RETURN_STORAGE_KEY,
      sanitizeReturnUrl(path ?? null)
    );
  } catch {
    /* private mode / quota */
  }
}

/** Read return path without clearing (survives refresh inside the viewer). */
export function peekExamViewerReturn(): string {
  try {
    return sanitizeReturnUrl(sessionStorage.getItem(RETURN_STORAGE_KEY));
  } catch {
    return DEFAULT_RETURN;
  }
}

/** Migrate legacy `?returnUrl=` into sessionStorage and strip it from the URL. */
export function migrateReturnUrlParam(searchParams: URLSearchParams): {
  next: URLSearchParams;
  changed: boolean;
} {
  const next = new URLSearchParams(searchParams);
  const legacy = next.get('returnUrl');
  if (!legacy) return { next, changed: false };
  storeExamViewerReturn(legacy);
  next.delete('returnUrl');
  return { next, changed: true };
}

export function parseExamViewerTab(
  raw: string | null | undefined
): ExamViewerTab {
  if (raw === 'views' || raw === 'report' || raw === 'generalViewer') return raw;
  // Legacy deep link: the Findings panel was removed; its values live on the report.
  if (raw === 'findings') return 'report';
  return 'generalViewer';
}

export type BuildExamViewerUrlInput = {
  studyUid: string;
  /** Stored in sessionStorage, never written into the URL. */
  returnUrl?: string | null;
  tab?: ExamViewerTab | null;
  /** Report sub-tab slug (`graphs`, `prenatal`, …) when tab=report */
  reportSection?: string | null;
  /** `collaboration` | `analysis` | `patient` */
  panel?: string | null;
  sopInstanceUID?: string | null;
  serie?: string | null;
  frameId?: string | null;
  /** Open Views → view details for this view_code */
  viewCode?: string | null;
  /** Seed General Viewer Smart Filter (comma-separated view codes) */
  filterViews?: string | null;
  /** Seed General Viewer Smart Filter (comma-separated structure category ids) */
  filterStructures?: string | null;
};

export function buildExamViewerSearchParams(
  input: BuildExamViewerUrlInput
): URLSearchParams {
  if (input.returnUrl != null) {
    storeExamViewerReturn(input.returnUrl);
  } else {
    storeExamViewerReturn(DEFAULT_RETURN);
  }

  const params = new URLSearchParams();
  params.set('StudyInstanceUIDs', input.studyUid);
  if (input.tab && input.tab !== 'generalViewer') {
    params.set('tab', input.tab);
  }
  if (input.reportSection && input.tab === 'report') {
    params.set('reportSection', input.reportSection);
  }
  if (input.panel) params.set('panel', input.panel);
  if (input.sopInstanceUID) params.set('sopInstanceUID', input.sopInstanceUID);
  if (input.serie && input.serie !== 'All') params.set('serie', input.serie);
  if (input.frameId) params.set('frameId', input.frameId);
  if (input.viewCode) {
    params.set('tab', 'views');
    params.set('viewCode', input.viewCode);
  }
  if (input.filterViews) params.set('filterViews', input.filterViews);
  if (input.filterStructures) params.set('filterStructures', input.filterStructures);
  return params;
}

export function buildExamViewerUrl(input: BuildExamViewerUrlInput): string {
  return `/exam/viewer?${buildExamViewerSearchParams(input).toString()}`;
}

/** Patch current viewer search params (never writes returnUrl into the URL). */
export function patchViewerSearchParams(
  current: URLSearchParams,
  patch: {
    tab?: ExamViewerTab | null;
    reportSection?: string | null;
    panel?: string | null;
    sopInstanceUID?: string | null;
    serie?: string | null;
    frameId?: string | null;
    viewCode?: string | null;
    filterViews?: string | null;
    filterStructures?: string | null;
  }
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete('returnUrl'); // hygiene: never keep return path in the address bar

  if (patch.tab !== undefined) {
    if (!patch.tab || patch.tab === 'generalViewer') next.delete('tab');
    else next.set('tab', patch.tab);
    // Report section only applies while on the Report main tab
    if (!patch.tab || patch.tab !== 'report') next.delete('reportSection');
  }

  if (patch.reportSection !== undefined) {
    if (!patch.reportSection) next.delete('reportSection');
    else next.set('reportSection', patch.reportSection);
  }

  if (patch.panel !== undefined) {
    if (!patch.panel) next.delete('panel');
    else next.set('panel', patch.panel);
  }

  if (patch.sopInstanceUID !== undefined) {
    if (!patch.sopInstanceUID) next.delete('sopInstanceUID');
    else next.set('sopInstanceUID', patch.sopInstanceUID);
  }

  if (patch.serie !== undefined) {
    if (!patch.serie || patch.serie === 'All') next.delete('serie');
    else next.set('serie', patch.serie);
  }

  if (patch.frameId !== undefined) {
    if (!patch.frameId) next.delete('frameId');
    else next.set('frameId', String(patch.frameId));
  }

  if (patch.viewCode !== undefined) {
    if (!patch.viewCode) {
      next.delete('viewCode');
    } else {
      next.set('viewCode', patch.viewCode);
      next.set('tab', 'views');
    }
  }

  if (patch.filterViews !== undefined) {
    if (!patch.filterViews) next.delete('filterViews');
    else next.set('filterViews', patch.filterViews);
  }

  if (patch.filterStructures !== undefined) {
    if (!patch.filterStructures) next.delete('filterStructures');
    else next.set('filterStructures', patch.filterStructures);
  }

  return next;
}

/** Remember last Report sub-tab so re-opening Report restores Graphs, etc. */
export function storeLastReportSection(slug: string | null | undefined): void {
  if (!slug) return;
  try {
    sessionStorage.setItem(REPORT_SECTION_STORAGE_KEY, slug);
  } catch {
    /* private mode / quota */
  }
}

export function peekLastReportSection(): string | null {
  try {
    return sessionStorage.getItem(REPORT_SECTION_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Build a navigate() target that updates query params without dropping the
 * hash. React Router's setSearchParams() navigates to `?…` only and clears
 * `#report-section` hashes — which breaks Report sub-tabs and browser Back.
 */
export function buildViewerLocation(
  location: { pathname: string; search: string; hash: string },
  patch: Parameters<typeof patchViewerSearchParams>[1],
  hash: string | null | undefined = undefined
): { pathname: string; search: string; hash: string } {
  const current = new URLSearchParams(
    location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search
  );
  const next = patchViewerSearchParams(current, patch);
  const search = next.toString();
  return {
    pathname: location.pathname,
    search: search ? `?${search}` : '',
    // undefined → keep current hash; null/'' → clear
    hash: hash === undefined ? location.hash : hash || '',
  };
}
